import { useMemo } from 'react';
import _sumBy from 'lodash/sumBy';

import { useGetAccountCredits } from ':cloud/queries/BillingQueries/useGetAccountCredits';
import { useGetUser } from ':cloud/queries/UserQueries/useGetUser';

const LOW_BALANCE_THRESHOLD = 0.8; // over 80% used

export function useAccountCreditsBalances() {
  const { activeOrg } = useGetUser();
  const orgId = activeOrg?.organizationId;
  const { accountCredits } = useGetAccountCredits(orgId);

  const accountCreditsIssued = useMemo(() => {
    return _sumBy(accountCredits, (credit) => +credit.amountIssued);
  }, [accountCredits]);
  const accountCreditsRemaining = useMemo(() => {
    return _sumBy(
      accountCredits.filter((credit) => !credit.creditType.startsWith('additional_')),
      (credit) => +credit.amountRemaining,
    );
  }, [accountCredits]);
  const accountCreditsUsed = 1 - accountCreditsRemaining / accountCreditsIssued;
  const isAccountCreditsBalanceLow = accountCreditsUsed >= LOW_BALANCE_THRESHOLD;
  const accountCreditsUsedPercentage = new Intl.NumberFormat('default', {
    style: 'percent',
    maximumFractionDigits: 0,
  }).format(accountCreditsUsed);

  const accountCreditTypes = useMemo(() => {
    // Get unique credit types
    return [...new Set(accountCredits.map((credit) => credit.creditType))];
  }, [accountCredits]);

  const additionalCredits: Record<string, number> = useMemo(() => {
    // Filter credits to only include those starting with 'additional_'
    const additionalCreditsList = accountCredits.filter((credit) =>
      credit.creditType.startsWith('additional_'),
    );
    return additionalCreditsList.reduce((acc, credit) => {
      acc[credit.creditType] = (acc[credit.creditType] || 0) + +credit.amountRemaining;
      return acc;
    }, {});
  }, [accountCredits]);

  return {
    accountCreditsIssued,
    accountCreditsRemaining,
    accountCreditsUsedPercentage,
    isAccountCreditsBalanceLow,
    accountCreditTypes,
    additionalCredits,
  };
}
